import {
	ConnectionManager,
	ConnectionRelation,
	GridTools,
	ModelData,
	ProductData,
	SceneObject,
	Product,
} from '@ikea-aoa/component-view-3d';
import { CoverItem, Platform, RangeSettings } from 'sofa-reborn-react';
import { ConnectionKitsManager } from '../../../util/SceneUtils/ConnectionKitsManager';
import { ModuleConnectionManager } from '../../../util/SceneUtils/ModuleConnectionManager';
import { ObjectManager } from '../ObjectManager';

type Props = {
	rangeSettings: RangeSettings;
	products: ProductData;
	sofaModels: ModelData;
	assetFolder: string | null;
	loadingModels: boolean;
	cover: CoverItem;
	selectedArmrest: Product | null;
	platform: Platform;
	showDebug?: boolean | undefined;
	specificItems: Map<string, SceneObject>;
};
export class AutomaticallyAddManager {
	private addedItems = new Array<SceneObject>();

	private gridTools: GridTools;
	private connectionManager: ConnectionManager;
	private moduleManager: ModuleConnectionManager;
	private connectionKitsManager: ConnectionKitsManager;
	private sceneItems: Map<string, SceneObject>;
	private isSeatLeft: boolean;

	constructor(private objectManager: ObjectManager, private props: Props) {
		this.gridTools = objectManager.gridTools;
		this.connectionManager = objectManager.connectionManager;
		this.moduleManager = objectManager.moduleManager;
		this.connectionKitsManager = objectManager.connectionKitsManager;
		this.sceneItems = objectManager.sceneItems;
	}

	/**
	 * Update props.
	 *
	 * @param props Props.
	 */
	public updateProps(props: Props): void {
		this.props = props;
	}

	/**
	 * Creates if needed.
	 */
	public create(): void {
		this.getAutoAddModels()
			.sort()
			.map((models) => this.createWithModel(models));
	}

	/**
	 * Removes.
	 * Removes only necessary if dragged item is passed.
	 *
	 * @param shouldRemoveAll If all items should be removed when deleting from popover menu.
	 * @param options Options.
	 * @param options.draggedItem Currently dragged item.
	 * @param options.isSmartMirror Is smart mirrored.
	 */
	public remove(
		shouldRemoveAll: boolean,
		options?: { draggedItem?: SceneObject; isSmartMirror?: boolean },
		swiperEventType?: 'click' | 'drag' | null
	): void {
		this.autoPopulateAddItems();
		if (this.addedItems.length) {
			if (!options?.draggedItem) {
				if (shouldRemoveAll) {
					this.removeAll();
				} else {
					this.addedItems = new Array<SceneObject>();
				}
			} else if (options?.draggedItem && this.shouldOnlyRemoveChildItems(options?.draggedItem)) {
				this.removeChildItems(options?.draggedItem);
			} else if (options?.isSmartMirror) {
				this.removeAll();
			} else {
				if (options?.draggedItem) {
					const draggedItemModelData = this.connectionManager.getModelData(
						options?.draggedItem?.articleId
					);
					if (draggedItemModelData && !draggedItemModelData.logic?.keepChildrenOnDrag) {
						this.removeNecessary(options?.draggedItem, false, swiperEventType);
					}
				}
			}
		}
	}

	/**
	 * Returns article.
	 */
	public getArticle(modelId?: string): string | undefined {
		const product = Object.values(this.props.products).find((product) => {
			if (this.isAutoConnect(product) && this.isCurrentColor(product)) {
				if (modelId) {
					const model = this.props.sofaModels[product.modelId];
					return modelId === model.modelId;
				}
				return false;
			}
			return false;
		});
		return product?.productId;
	}

	/**
	 * Checks if it should add automatically.
	 *
	 * @param modelId Model identity of item.
	 * @returns If add automatically should activate.
	 */
	public isAddAutomatically(modelId?: string): boolean {
		if (modelId) {
			if (this.props?.rangeSettings?.addArmrestsAutomatically) {
				// Legacy function for adding armrest.
				return modelId.includes('ARMREST');
			}
			return this.props?.rangeSettings?.addAutomatically?.includes(modelId) as boolean;
		}
		return (
			Array.isArray(this.props?.rangeSettings?.addAutomatically) ||
			(this.props?.rangeSettings?.addArmrestsAutomatically as boolean) // Legacy function for adding armrest.
		);
	}

	/**
	 * Moves sceneObject.
	 *
	 * @param moveItem Scene object that should be moved.
	 */
	public move(moveItem: SceneObject): boolean {
		if (this.connectionManager.isAutoConnectMovable(moveItem) && moveItem.parentId) {
			const availableRelations = this.getAvailableConnectionRelations(moveItem);
			const connection = this.connectionManager.getConnection(moveItem.id, moveItem.parentId);
			if (availableRelations && availableRelations.length > 1 && connection) {
				const relation = availableRelations.find(
					(relation) => relation.parent !== connection.parent
				);
				const parent = this.sceneItems.get(moveItem.parentId);
				if (relation && parent) {
					this.moduleManager.disconnect(moveItem);
					this.moduleManager.connect(moveItem, parent, relation);
				}
				return true;
			}
		}
		return false;
	}

	/**
	 * Moves sceneObject.
	 *
	 * @param moveItem Scene object that should be moved.
	 */
	public moveBackrest(moveItem: SceneObject, parentItem: SceneObject): boolean {
		let availablePoints: string[] = [];
		let isMoved = false;
		const moveParentId = moveItem.parentId;
		const moveId = moveItem.id;
		this.sceneItems.forEach((item) => {
			const nrOfConnections = this.moduleManager.getAllConnectedModules(item.id);
			if (
				nrOfConnections.length < 2 &&
				moveId !== item.id &&
				moveParentId !== item.id &&
				!item.articleId.includes('backrest')
			) {
				const childModel = this.connectionManager.getModelData(moveItem.articleId);
				const parentModel = this.connectionManager.getModelData(item.articleId);
				if (childModel && parentModel && !parentModel.modelId.includes('backrest')) {
					availablePoints = this.moduleManager
						.getAvailableConnectionPoints(item)
						.filter((point) => point === 'RightClamp' || point === 'LeftClamp');
					const relation = this.connectionManager
						.getRelations(childModel, parentModel)
						.filter((rel) => availablePoints.includes(rel.parent));
					const parent = this.sceneItems.get(item.id);
					if (relation.length > 0 && parent) {
						this.moduleManager.disconnect(moveItem);
						this.moduleManager.connect(moveItem, parent, relation[0]);
						isMoved = true;
					}

					// Change connection for 1,5 seat (rotate it)
					const parentItemModel = this.connectionManager.getModelData(parentItem.articleId);
					if (parentItemModel && relation.length > 0) {
						const childLeft = relation[0].parent === 'LeftClamp';
						this.isSeatLeft = childLeft;
						const parentRelation = this.connectionManager
							.getRelations(parentItemModel, parentModel)
							.filter(
								(rel) =>
									rel.child === (childLeft ? 'FrontClamp' : 'BackClamp') &&
									rel.parent !== relation[0].parent
							);
						const connectedModule = this.moduleManager.getAllConnectedModules(parentItem.id);
						if (parentRelation.length > 0 && connectedModule.length > 0) {
							this.moduleManager.disconnect(parentItem);
							this.moduleManager.connect(parentItem, connectedModule[0].object, parentRelation[0]);
						}
					}
				}
			}
		});
		return isMoved;
	}

	/**
	 * Changes seat connection.
	 *
	 * @param parentItem Scene object that should be moved.
	 */
	public changeSeatConnection(parentItem: SceneObject): boolean {
		const isMoved = false;
		this.sceneItems.forEach((item) => {
			if (parentItem.id !== item.id && !item.articleId.includes('backrest')) {
				const parentModel = this.connectionManager.getModelData(item.articleId);
				if (parentModel && !parentModel.modelId.includes('backrest')) {
					// Change connection for 1,5 seat (rotate it)
					const parentItemModel = this.connectionManager.getModelData(parentItem.articleId);
					if (parentItemModel) {
						const parentRelation = this.connectionManager
							.getRelations(parentItemModel, parentModel)
							.filter((rel) =>
								this.isSeatLeft
									? rel.parent === 'RightClamp' && rel.child === 'LeftClamp'
									: rel.parent === 'LeftClamp' && rel.child === 'RightClamp'
							);
						const connectedModule = this.moduleManager.getAllConnectedModules(parentItem.id);
						if (parentRelation.length > 0 && connectedModule.length > 0) {
							this.moduleManager.disconnect(parentItem);
							this.moduleManager.connect(parentItem, connectedModule[0].object, parentRelation[0]);
						}
					}
				}
			}
		});
		return isMoved;
	}

	/**
	 * Checks if the scene object could move.
	 *
	 * @param moveItem Scene object that is to be checked.
	 * @returns IF it could move.
	 */
	public isMovable(moveItem: SceneObject): boolean {
		if (this.connectionManager.isAutoConnectMovable(moveItem)) {
			const availableRelations = this.getAvailableConnectionRelations(moveItem);
			if (availableRelations && availableRelations.length > 1) {
				return true;
			}
		}
		return false;
	}

	private getAutoAddModels(): string[] {
		if (
			this.props.rangeSettings.addAutomatically &&
			this.props.rangeSettings.addAutomatically.length
		) {
			return this.props.rangeSettings.addAutomatically;
		} else if (this.props.rangeSettings.addArmrestsAutomatically) {
			// Legacy function for adding armrest.
			if (this.props.selectedArmrest) {
				return [this.props.selectedArmrest.modelId];
			}
			return Object.values(this.props.products).reduce((autoModels: string[], product: Product) => {
				if (this.isAutoConnect(product) && this.isCurrentColor(product)) {
					autoModels.push(product.modelId);
				}
				return autoModels;
			}, []);
		}
		return [];
	}

	private isAutoConnect(product: Product): boolean {
		const model = this.props.sofaModels[product.modelId];
		return !!model?.logic?.autoConnect;
	}

	private isCurrentColor(product: Product): boolean {
		const colorId = this.objectManager.getColorId();
		if (colorId) {
			return product.filter.color === colorId;
		}
		return false;
	}

	private createWithModel(modelId: string): void {
		if (!this.isAddAutomatically(modelId)) {
			return;
		}
		if (this.hasNoAutomaticallyAddAvailability()) {
			this.remove(this.props.rangeSettings.rangeName === 'JÄTTEBO');
			return;
		}

		const articleId = this.getArticle(modelId);
		if (!articleId) {
			return;
		}

		this.createAutoObjects(articleId);
	}

	private createAutoObjects(articleId: string): void {
		const gridBoxes = this.gridTools.createGridBoxes(this.sceneItems);
		const filter = {
			valid: true,
			available: true,
		};
		const relationMap = this.connectionManager.getAllRelations(articleId, filter, undefined, false);

		relationMap.forEach((relations, parentId) => {
			relations.forEach((relation) => {
				const parent = this.sceneItems.get(parentId);
				if (parent && relation.type !== 'notPrioritized') {
					const connected = this.connectionManager.getDirectlyConnected(parentId);
					const transform = this.connectionManager.getConnectedWorldTransform(
						articleId,
						parent,
						relation
					);
					const intersections = this.gridTools.checkIntersection(
						articleId,
						transform,
						'default',
						0.3,
						gridBoxes,
						[parentId, ...connected]
					);
					if (this.checkLimit(connected, articleId, parent.articleId)) {
						this.createAutoObject(intersections, articleId, parentId, relation);
					}
				}
			});
		});
	}

	private checkLimit(connected: string[], articleId: string, parentArticleId: string): boolean {
		const product = this.props.products[articleId];
		const parentProduct = this.props.products[parentArticleId];
		const model = this.props.sofaModels[parentProduct.modelId];
		if (
			model?.logic?.autoConnectLimit &&
			product.filter.family &&
			typeof model.logic.autoConnectLimit[product.filter.family] === 'number'
		) {
			return (
				model.logic.autoConnectLimit[product.filter.family] >
				this.getNumberOfConnections(connected, product)
			);
		}
		return true;
	}

	private getNumberOfConnections(connected: string[], product: Product): number {
		return connected.filter((id) => {
			const sceneItem = this.sceneItems.get(id);
			if (sceneItem?.articleId) {
				return this.props.products[sceneItem.articleId].filter.family === product.filter.family;
			}
			return false;
		}).length;
	}

	private createAutoObject(
		intersections: string[],
		articleId: string,
		parentId: string,
		relation: ConnectionRelation
	): void {
		if (!intersections.length) {
			const sceneObject = this.objectManager.createObject(articleId);
			if (sceneObject) {
				this.sceneItems.set(sceneObject.id, sceneObject);
				const parent = this.sceneItems.get(parentId);
				if (parent) {
					this.moduleManager.connect(sceneObject, parent, relation);
					this.addedItems.push(sceneObject);
				}
			}
		}
	}

	private hasNoAutomaticallyAddAvailability(): boolean {
		const sceneItemArray = Array.from(this.sceneItems.values());

		if (this.props.rangeSettings.hasModulesWithoutIncludedArmrest) {
			const onlyModuleWithNoArmrestsOnScene = sceneItemArray.every((sceneItem: SceneObject) => {
				const modelData = this.connectionManager.getModelData(sceneItem.articleId);
				return (
					modelData &&
					(modelData.logic?.noArmrestsIncluded ||
						modelData.modelId.includes('FOOTSTOOL') ||
						modelData.modelId.includes('ARMREST'))
				);
			});

			if (onlyModuleWithNoArmrestsOnScene) {
				return true;
			}
		}
		return sceneItemArray.every((sceneItem: SceneObject) => {
			const modelData = this.connectionManager.getModelData(sceneItem.articleId);
			return modelData && this.isAddAutomatically(modelData.modelId);
		});
	}

	private shouldOnlyRemoveChildItems(draggedItem?: SceneObject): boolean {
		const modelData = this.connectionManager.getModelData(draggedItem?.articleId || '');
		return !!modelData && !!modelData?.logic?.onlyRemoveChildItems;
	}

	private removeChildItems(draggedItem: SceneObject): void {
		let modelData = this.connectionManager.getModelData(draggedItem.articleId);
		if (modelData && modelData.logic) {
			if (modelData.logic.onlyRemoveChildItems) {
				const destroy = new Set<SceneObject>();
				this.sceneItems.forEach((sceneItem: SceneObject) => {
					if (sceneItem && sceneItem.parentId) {
						const parent = this.sceneItems.get(sceneItem.parentId);
						if (parent) {
							modelData = this.connectionManager.getModelData(parent.articleId);
							if (
								!!modelData &&
								!!modelData?.logic?.onlyRemoveChildItems &&
								!modelData.logic?.keepChildrenOnDrag
							) {
								if (sceneItem.parentId === draggedItem.id) {
									destroy.add(sceneItem);
								}
							}
						}
					}
				});
				this.destroy(destroy);
			}
			if (!modelData.logic.autoConnectMovable) {
				this.removeNecessary(draggedItem, !modelData.logic.keepChildrenOnDrag);
			}
		}
	}

	private removeNecessary(
		draggedItem: SceneObject,
		removeChildItems?: boolean,
		swiperEventType?: 'click' | 'drag' | null
	): void {
		const { articleId } = draggedItem;
		const childModel = this.connectionManager.getModelData(articleId);
		if (!childModel) {
			return;
		}
		const destroy = new Set<SceneObject>();
		this.addedItems.forEach((o) => {
			if (o && o.parentId) {
				const parent = this.sceneItems.get(o.parentId);
				if (parent) {
					const parentModel = this.connectionManager.getModelData(parent.articleId);
					if (parent == draggedItem) {
						if (parentModel && !parentModel.logic?.keepChildrenOnDrag) {
							destroy.add(o);
						}
						return;
					}
					if (this.shouldSkipCheck(removeChildItems, parent)) {
						return;
					}
					if (parentModel) {
						const relation = this.connectionManager.getConnection(o.id, o.parentId);
						if (relation) {
							const relations = this.connectionManager.getRelations(childModel, parentModel);
							relations.forEach((r) => {
								if (
									r.parent == relation.parent &&
									this.connectionManager.isValid(r) &&
									this.connectionKitsManager.hasAvailableConnectionKits(
										articleId,
										parent,
										null,
										this.props.rangeSettings.simpleConnectionKitsLogic || false
									)
								) {
									const doNotRemove =
										this.props.rangeSettings.rangeName === 'JÄTTEBO' && swiperEventType === 'click';
									if (!doNotRemove) {
										destroy.add(o);
									}
								}
							});
						}
					}
				}
			}
		});
		this.destroy(destroy);
	}

	private shouldSkipCheck(removeChildItems: boolean | undefined, parent: SceneObject) {
		let skip = false;
		if (removeChildItems) {
			parent.node?.children.forEach((childItem) => {
				const child = this.sceneItems.get(childItem.id);
				if (child) {
					const availableConnections = this.getAvailableConnectionRelations(child)?.length || 0;
					if (availableConnections <= 1) {
						skip = true;
					}
				}
			});
		}
		return skip;
	}

	private getAvailableConnectionRelations(
		sceneObject: SceneObject
	): ConnectionRelation[] | undefined {
		if (sceneObject.parentId) {
			const relationMap = this.connectionManager.getAllRelations(
				sceneObject,
				{
					valid: true,
					available: true,
				},
				true,
				true,
				true
			);
			return relationMap.get(sceneObject.parentId);
		}
		return undefined;
	}

	private destroy(destroy: Set<SceneObject>): void {
		destroy.forEach((o) => {
			const i = this.addedItems.indexOf(o);
			if (i !== -1) {
				this.addedItems.splice(i, 1);
			}
			this.objectManager.destroyObject(o);
		});
	}

	private removeAll(): void {
		this.addedItems.forEach((o) => this.objectManager.destroyObject(o));
		this.addedItems = new Array<SceneObject>();
	}

	private autoPopulateAddItems(): void {
		if (this.addedItems.length === 0 && this.isAddAutomatically()) {
			this.sceneItems.forEach((item) => {
				const model = this.connectionManager.getModelData(item.articleId);
				if (model && this.isAddAutomatically(model.modelId)) {
					this.addedItems.push(item);
				}
			});
		}
	}
}
